import {
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	Input,
	OnInit,
	TemplateRef,
	ViewEncapsulation,
} from "@angular/core";
import { createRxValue, createScheduleDayParserFromUser } from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { HomeTimerComponent } from "@app/pages/home-page/home-timer/home-timer.component";
import { MyTimesQuery, Time, UserSettingsQuery } from "@timeghost/web-api";
import { isSameDay, startOfDay } from "date-fns/esm";
import { combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { EntryItem } from "../time-tracker-table.component";
import { TimeTrackerTableCardContentDirective } from "../time-tracker-table-card-content.directive";

@Component({
	selector: "app-time-tracker-table-card",
	templateUrl: "./time-tracker-table-card.component.html",
	styleUrls: ["./time-tracker-table-card.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	host: {
		class: "tg-time-card-section",
	},
})
export class TimeTrackerTableCardComponent implements OnInit {
	@ContentChild(TimeTrackerTableCardContentDirective, { read: TemplateRef, static: true }) template: TemplateRef<any>;
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService,
		private parent: HomeTimerComponent,
		private myTimesQuery: MyTimesQuery,
		private clock: ClockService
	) {}
	ngOnInit(): void {
		this._data.asObservable().subscribe(() => {
			this.parent.updateSumOfWeeks.update(undefined);
		});
	}
	private _data = createRxValue<EntryItem>(null);

	@Input()
	public get data(): EntryItem {
		return this._data.value;
	}
	public set data(v: EntryItem) {
		this._data.next(v);
	}
	readonly data$ = this._data.asObservable();
	readonly comegoEnabled$ = this.appService.comegoEnabled$;

	private readonly runningTimes$ = combineLatest([
		this.data$,
		this.myTimesQuery.selectAll({ filterBy: (x) => !x.end }),
		this.clock.now$,
	]).pipe(
		map(([entry, times, now]) => {
			if (!entry?.date) return [] as Time[];
			return times
				.filter((t) => t.start && isSameDay(new Date(t.start), entry.date))
				.map((t) => ({
					...t,
					end: now.toISOString(),
					timeDiff: Math.max(0, (now.getTime() - new Date(t.start).getTime()) / 1000),
				}));
		})
	);

	readonly schedule$ = combineLatest([this.data$, this.userSettingsQuery.select(), this.runningTimes$]).pipe(
		map(([entry, user, runningTimes]) => {
			if (!entry || !entry.times?.length) return null;
			const firstTime = entry.date,
				now = Date.now();
			if (!user.workspace?.schedules?.workspace?.enabled) return null;
			const parser = createScheduleDayParserFromUser(user, firstTime, undefined, true);
			if (!parser) return null;
			const completedTimes = entry.times.reduce((acc, r) => {
				if (r.times.length) acc.push(...r.times);
				return acc;
			}, [] as Time[]);
			const stats = parser([...completedTimes, ...runningTimes], firstTime);
			if (!stats?.enabled) return stats;
			const dateOfReference = startOfDay(firstTime.getTime()),
				isPastSchedule = now > dateOfReference.getTime(),
				pastFailure = isPastSchedule && stats.usage < stats.cap;
			return {
				...stats,
				percent: stats.usage / stats.cap,
				isPastSchedule,
				pastFailure,
				graphState: pastFailure ? "primary" : stats.usage < stats.cap ? "accent" : "success",
			};
		})
	);
	readonly data$stats = combineLatest([this.data$, this.runningTimes$]).pipe(
		map(([entry, runningTimes]) => {
			if (!entry?.times) {
				return {
					billable: 0,
					billablePercentage: 0,
					billablePercentageFormat: "0",
					nonBillable: 0,
					nonBillablePercentage: 0,
					timeSum: 0,
				};
			}
			const times = entry.times
				.filter((x) => x.min && x.max)
				.reduce((l, r) => l.concat(r.times), <Time[]>[])
				.concat(runningTimes);
			const timeSum = times.filter((x) => x.timeDiff > 0).reduce((l, r) => (l += ~~r.timeDiff), 0);
			const billable = times.filter((x) => x.timeDiff > 0 && x.billable).reduce((l, r) => (l += ~~r.timeDiff), 0);
			const nonBillable = times
				.filter((x) => x.timeDiff > 0 && !x.billable)
				.reduce((l, r) => (l += ~~r.timeDiff), 0);
			const billablePercentage = (billable / timeSum) * 100;
			return {
				billable,
				billablePercentage,
				billablePercentageFormat: billablePercentage.toFixed(0),
				nonBillable,
				nonBillablePercentage: (nonBillable / timeSum) * 100,
				timeSum,
			};
		})
	);
}
